import { useCallback, useEffect, useMemo, useState } from 'react';
import { message } from 'ui/message';

const DROPBOX_SDK_URL = 'https://www.dropbox.com/static/api/2/dropins.js';
const SCRIPT_ID = 'dropboxjs';
const apiKey = String(import.meta.env.VITE_APP_DROPBOX_API_KEY);

const useDropboxChooser = () => {
  // TODO: Checking for the <script> element does not indicate that the SDK has finished loading.
  // If another component mounts while the script is in flight, isSdkLoaded is incorrectly initialized to true
  // without attaching a load listener or waiting for window.Dropbox.
  // Check Boolean(window.Dropbox) to determine readiness, attach a listener to any existing pending script,
  // and remove event listeners during cleanup.
  const [isSdkLoaded, setIsSdkLoaded] = useState(() => Boolean(document.querySelector(`#${SCRIPT_ID}`)));

  useEffect(() => {
    if (isSdkLoaded) {
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = DROPBOX_SDK_URL;
    script.dataset.appKey = apiKey;
    script.async = true;
    script.addEventListener('load', () => {
      setIsSdkLoaded(true);
    });
    script.addEventListener('error', () => {
      message.error('Dropbox SDK failed to load');
    });
    document.head.append(script);
  }, [isSdkLoaded]);

  const choose = useCallback(
    (success: (data: Array<{ link: string; name: string }>) => void, cancel?: () => void) => {
      if (!isSdkLoaded) {
        message.error('Dropbox is not loaded');
        return false;
      }

      if (!window.Dropbox) {
        message.error('Dropbox is not loaded');
        return false;
      }

      window.Dropbox.choose({
        cancel,
        linkType: 'direct',
        success,
      });

      return true;
    },
    [isSdkLoaded],
  );

  return useMemo(() => ({ choose, isSdkLoaded }), [choose, isSdkLoaded]);
};

export default useDropboxChooser;
